/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { Button } from 'reactstrap';
import { AccessType, MetricGroup } from '../../types/metricGroup';
import { CardGridButtonProps, CardGridItemProps } from '@features/survey-scope/CardGrid';
import { getCardIcon } from '@features/survey-scope/CustomMetricCards';
import { getMetricGroupCountWarning, getMetricGroupUnitCount } from '@g17eco/molecules/metric-group/metricGroupCountWarning';
import { PACK, QUESTION, SURVEY } from '@constants/terminology';
import { InheritedIcon } from '@features/survey/scope-group/InheritedIcon';
import { getVariant } from '@utils/metricGroup';

export enum CardGridButtonType {
  Duplicate = 'duplicate',
  Edit = 'edit',
  ReadOnly = 'readonly',
  Delete = 'delete',
  InheritedTooltip = 'inherited-tooltip',
}

type ClassifyCardGridButton = CardGridButtonProps & {
  type: CardGridButtonType;
};

export interface CustomMetricCardProps extends CardGridItemProps {
  accessType?: AccessType;
}

export interface MetricGroupsProps {
  metricGroups: MetricGroup[];
  handleEditMetricGroup: (groupId: string) => void;
  handleDeleteMetricGroup: (groupId: string) => void;
  handleCreateMetricGroup: () => void;
  handleDuplicateMetricGroup: (groupId: string) => void;
}

export const getCustomMetricGroups = ({
  metricGroups,
  handleCreateMetricGroup,
  handleDeleteMetricGroup,
  handleDuplicateMetricGroup,
  handleEditMetricGroup,
}: MetricGroupsProps) => {

  const getButtons: (metricGroup: MetricGroup) => CardGridButtonProps[] = (metricGroup) => {
    const isAssigned = metricGroup.accessType === AccessType.Assigned;
    const variant = getVariant(metricGroup);
    const buttons: ClassifyCardGridButton[] = [
      {
        type: CardGridButtonType.Duplicate,
        tooltip: metricGroup.groupName ? `Duplicate ${metricGroup.groupName}` : undefined,
        button: (
          <Button
            outline
            onClick={() => handleDuplicateMetricGroup(metricGroup._id)}
            className='icon-button card-grid-button'
          >
            <i className='fal fa-copy'></i>
          </Button>
        ),
      },
      {
        type: CardGridButtonType.Edit,
        tooltip: isAssigned ? variant.assignedTooltip : variant.editTooltip,
        button: (
          <Button
            outline
            onClick={() => handleEditMetricGroup(metricGroup._id)}
            className='icon-button card-grid-button'
            disabled={variant.editDisabled}
          >
            <i className='fal fa-cog' />
          </Button>
        ),
      },
      {
        type: CardGridButtonType.ReadOnly,
        icon: <i className='fa-light fa-eye' />,
        tooltip: 'View inherited Custom Metric',
        onClick: () => handleEditMetricGroup(metricGroup._id),
      },
      {
        type: CardGridButtonType.Delete,
        tooltip:
          typeof variant.deleteTooltip === 'function'
            ? variant.deleteTooltip(metricGroup.groupName)
            : variant.deleteTooltip,
        button: (
          <Button
            outline
            onClick={() => handleDeleteMetricGroup(metricGroup._id)}
            className='icon-button card-grid-button'
            color={variant.deleteDisabled ? undefined : 'danger'}
            data-testid='delete-metric-group-btn'
            disabled={variant.deleteDisabled}
          >
            <i className='fal fa-trash-can'></i>
          </Button>
        ),
      },
      {
        type: CardGridButtonType.InheritedTooltip,
        tooltip: 'This module has been inherited and cannot be deleted or edited',
        button: (
          <Button color='transparent' disabled className='px-2'>
            <i className='fa-light fa-circle-info'></i>
          </Button>
        ),
      },
    ];

    switch (metricGroup.accessType) {
      case AccessType.Custom:
        return buttons.filter((button) =>
          [CardGridButtonType.Duplicate, CardGridButtonType.Edit, CardGridButtonType.Delete].includes(button.type)
        );
      case AccessType.Inherited:
        return buttons.filter((button) =>
          [CardGridButtonType.Duplicate, CardGridButtonType.ReadOnly, CardGridButtonType.InheritedTooltip].includes(
            button.type
          )
        );
      case AccessType.Assigned:
        return buttons.filter((button) => [CardGridButtonType.Edit].includes(button.type));
      default:
        return [];
    }
  };

  // Group "Inherited" and "Custom" access type as 'custom' for filtering
  const getCardAccessType = (metricGroup: MetricGroup) => {
    if (!metricGroup.accessType || [AccessType.Inherited, AccessType.Custom].includes(metricGroup.accessType)) {
      return AccessType.Custom;
    }
    return metricGroup.accessType;
  };

  const cards: CustomMetricCardProps[] = metricGroups.map((metricGroup: MetricGroup) => {
    const isInherited = metricGroup.accessType === AccessType.Inherited;
    const isAssigned = metricGroup.accessType === AccessType.Assigned;
    const name = `${isAssigned ? `${metricGroup.initiative?.name} assigned: ` : ''}${metricGroup.groupName}`;
    const card: CustomMetricCardProps = {
      key: `scope-cardgriditem-metricgroup-${metricGroup._id}`,
      title: <span>{name}</span>,
      sortTitle: name,
      description: metricGroup.description ?? '',
      icon: getCardIcon(metricGroup),
      inheritedIcon: isInherited ? <InheritedIcon /> : null,
      unitCount: getMetricGroupUnitCount(metricGroup),
      unitCountSuffix: getMetricGroupCountWarning(metricGroup),
      unitName: QUESTION.CAPITALIZED_PLURAL,
      buttons: getButtons(metricGroup),
      scopeTag: metricGroup._id,
      inScope: false,
      isPartial: false,
      accessType: getCardAccessType(metricGroup),
    };
    return card;
  });

  // Add create new pack button to the first group
  cards.unshift({
    key: 'scope-cardgriditem-metricgroup-createnew',
    title: (
      <span data-testid='create-metric-group-card'>
        Create a new {SURVEY.ADJECTIVE} {PACK.SINGULAR}
      </span>
    ),
    sortTitle: `000000 Create a new ${SURVEY.ADJECTIVE} ${PACK.SINGULAR}`,
    subtitle: `Click here if you would like to create a new ${SURVEY.ADJECTIVE} ${PACK.SINGULAR} of custom metrics`,
    icon: (
      <span className='new-survey-pack-icon' onClick={handleCreateMetricGroup}>
        <i className='fa fa-plus' />
      </span>
    ),
    unitCount: undefined,
    unitName: '',
    buttons: [
      {
        icon: <span className='px-3'>Create</span>,
        tooltip: 'Create metric question groups',
        onClick: handleCreateMetricGroup,
      },
    ],
    scopeTag: '',
    inScope: false,
    isPartial: false,
    accessType: AccessType.Custom,
  });

  return cards;
};
